import waitFor from '@iez/lib/wait-for/'
import { reactive, computed, toRefs, watch } from '@vue/composition-api'
import { isEmpty } from 'lodash'
import data from '@iez/lib/data/'
import { isEqual } from 'lodash'
import Vue from 'vue'

/**
 * Backend utility to fetch data snapshot.
 * And compute patch from (UI) new values.
 * Useful for Profile Edit page.
 */
/**
 * props = { a: { default: '', get: () => '' } }
 */
export default ({
  getId,
  props
}) => {
  /**
   * Create state.
   */
  const state = reactive({
    id: null,

    // data backend snapshot
    snapshot: {},

    isReady: computed(() => !isEmpty(state._data)),

    data: Object.fromEntries(
      Object.entries(
        props
      ).map(([key, { defaultValue }]) => [
        key,
        computed({
          get: () => state._data[key] ?? defaultValue,
          set: value => {
            state._data[key] = value
          }
        })
      ])
    ),

    /**
     * Setup with default values.
     */
    _data: Object.fromEntries(
      Object.entries(
        props
      ).map(([key, { defaultValue }]) => [
        key,
        defaultValue
      ])
    ),

    /**
     * For selects, items is possible choices
     */
    itemsRaw: Object.entries(
      props
    ).map(([key, { aggregation }]) => [
      key,
      aggregation
        ? data.get({
          root: aggregation.child.name,
          isCalledFromComponent: false,
          fields: [
            `id`,
            aggregation.child.labelProp
          ]
        }) : null
    ]).filter(
      ([_, value]) =>
        !!value
    ),
    items: computed(
      () => Object.fromEntries(
        state.itemsRaw.map(([
          key,
          computedValue
        ]) => [
          key,
          (computedValue?.value ?? []).map(
            item => ({
              id: item.id,
              label: item[props[key].aggregation.child.labelProp]
            })
          )
        ])
      )
    ),

    /**
     * Compute patch.
     * Take new values if different from snapshot ones.
     */
    _patch: computed(
      () => state.isReady
        ? Object.fromEntries(
          Object.entries(
            state.data
          ).map(
            ([key, value]) => [
              (
                props[key].type // si c'est typé, la valeur du snapshot est un objet { src, id }
                  ? state.snapshot[key]?.src !== value
                  : Array.isArray(
                    value
                  ) ? !isEqual(
                    value
                      .map(({ id }) => id)
                      .sort(),
                    (state.snapshot[key] ?? [])
                      .map(({ id }) => id)
                      .sort()
                  ) : state.snapshot[key] !== value
              ) ? key
                : null,
              value
            ]
          ).filter(([key]) => !!key)
        ) : null
    ),

    patch: computed(() => isEmpty(state._patch) ? null : state._patch),

    hasChanged: false
  })

  watch(
    () => state.patch,
    patch => {
      state.hasChanged = !!patch
    }
  )

  /**
   * Update values with backend data.
   */
  // next tick in case dependencies doesn't exist immediatly
  // case using reactive state, constructed with fromDataState
  /**
   * const state = reactive({
   *   me: ...,
   *   meDataState: fromDataState({
   *     getId: () => state....
   *   })
   * })
   */
  Vue.nextTick(() => {
    if (!getId) {
      return
    }
    waitFor({
      dependencies: getId
    }).then(async id => {
      await Promise.all(
        state.itemsRaw.map(([
          _,
          computedValue
        ]) => waitFor({
          dependencies: () => !!computedValue?.value
        }))
      )

      state.id = id

      // Hydrate snapshot.
      state.snapshot = Object.fromEntries(
        Object.entries(
          props
        ).map(([key, { get }]) => [
          key,
          props[key].aggregation
            ? state.items[key]
              .map(
                item => ({
                  item,
                  link: get().find(
                    link => link[
                      props[key].aggregation.child.idProp
                    ] === item.id
                  )
                })
              ).map(({ item, link }) =>
                link
                  ? {
                    ...item,
                    linkId: link.id
                  } : null
              ).filter(Boolean) : get()
        ])
      )

      /**
       * Merge values from snapshot.
       * Take current if user updated (case of InputImage) or snapshot one.
       * If prop is file (image or video) take src
       */
      Object.entries(
        state.snapshot
      ).forEach(([key, value]) => {
        // FIXME: is it still relevant,
        // if we disabled fields until load ?
        if (true || state._data[key] === props[key].defaultValue) {
          // user hasn't updated that value
          // can safely hydrate from snapshot
          state._data[key] = (
            props[key].type // if file take src
              ? value?.src
              : value
          ) ?? props[key].defaultValue ?? null
        }
      })
    })
  })

  const getIdFromUploadedFile = async ({ file, type, key }) => {
    console.log('Get Id from Uploaded file', file, type, key)
    if (state.snapshot[key]) {
      // if isCreating no need to remove previous image
      console.log('try remove file')
      removeFile({ key }) // removing file optimistically
    }

    console.log('Uplpading...', {
      file: (typeof file === `string` && file.startsWith(`#`))
        ? {
          placeholder: file
        } : file,
      type
    })
    const upload = data.upload({
      file: (typeof file === `string` && file.startsWith(`#`))
        ? {
          placeholder: file
        } : file,
      type
    })

    await waitFor({
      dependencies: () => upload.progress === 100
    })

    return upload.id
  }

  const removeFile = async ({ key }) => {
    if (state.snapshot[key]) {
      data.remove({
        root: `${props[key].type}s`,
        ids: state.snapshot[key].id,
        parents: []
      })
    }
    return null
  }

  const patchArray = async ({
    aggregation,
    before,
    after
  }) => {
    const afterIds = after.map(({ id }) => id)
    const beforeIds = before.map(({ id }) => id)

    // new items (not on before but in after)
    // need id
    const toAdd = afterIds.filter(id => !beforeIds.includes(id))

    // obsolete items (on before, not in after)
    // need link id
    const toRemove = before
      .filter(({ id: beforeId }) => !afterIds.includes(beforeId))
      .map(({ linkId }) => linkId)

    console.log({
      toAdd,
      toRemove
    })

    await Promise.all([
      toAdd.length ? data.set({
        root: aggregation.link.name,
        items: toAdd.map(
          id => ({
            [aggregation.root.idProp]: state.id,
            [aggregation.child.idProp]: id
          })
        ),
        fields: [
          `id`,
          aggregation.root.idProp,
          aggregation.child.idProp
        ],
        parents: [{
          root: aggregation.root.name,
          id: state.id,
          field: aggregation.link.name
        }]
      }) : null,
      data.remove({
        root: aggregation.link.name,
        ids: toRemove,
        parents: [{
          root: aggregation.root.name,
          id: state.id
        }]
      })
    ])

  }

  /**
   * Upload files if needed.
   * Return patch with those files id.
   */
  const smartPatch = async () =>
    state.patch
      ? Object.fromEntries(
        await Promise.all(
          Object.entries(
            state.patch
          ).map(async ([key, value]) =>
            props[key].type
              ? [
                // append Id to prop bc we want to return file Id
                `${key}Id`,
                value
                  ? await getIdFromUploadedFile({ key, file: value, type: props[key].type })
                  : await removeFile({ key })
              ] : props[key].aggregation
                ? await (async() => [
                  null,
                  await patchArray({
                    aggregation: props[key].aggregation,
                    before: state.snapshot[key],
                    after: state.data[key]
                  })
                ])() : [
                  key,
                  value
                ]
          )
        )
      ) : null

  const canMutate = computed(
    () => state.hasChanged || !getId
  )

  const mutate = async ({
    root,
    additionalPayload,
    fields
  }) => {
    const patch = await smartPatch()
    if (!getId) {
      await data.set({
        root,
        items: [{
          ...patch,
          ...additionalPayload
        }],
        fields
      })
    } else {
      const id = getId()
      await data.update({
        root,
        id,
        _set: patch,
        fields
      })
    }
  }

  return {
    ...toRefs(state),
    smartPatch,
    canMutate,
    mutate
  }
}
